
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { MailIcon } from '@heroicons/react/outline';
import Link from 'next/link';
import { useRouter } from 'next/router';
import Trans from 'next-translate/Trans';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocalStorage } from 'react-use';
import clsxm from '@/lib/clsxm';
import { useLastUsedLoginMethod } from '@/hooks/useLastUsedLoginMethod';
import { useCopyGap } from '@/components/agents/primitives';
import AuthLayout, { TermsNote } from '@/components/auth/AuthLayout';
import { type AuthErrorReason, describeAuthMessage, EXISTING_ACCOUNT_PAGE, isOAuthReason, } from '@/components/auth/authMessages';
import AuthTurnstile from '@/components/auth/AuthTurnstile';
import { buildUniqueEmailHistory, EMAIL_HISTORY_LIMIT, inboxProviderFor, normalizeEmail, } from '@/components/auth/emailHistory';
import { AuthButton, AuthHeading, buttonClass, fieldClass, fieldInvalid, GoogleGlyph, labelClass, linkClass, Notice, OrDivider, SentConfirmation, StatusIcon, tone, useAuthT, useAuthTrack, } from '@/components/auth/kit';
import OAuthButtons from '@/components/auth/OAuthButtons';
import PasswordField from '@/components/auth/PasswordField';
import LastUsedBadge from '@/components/LastUsedBadge';
import { isTurnstileEnabled, TURNSTILE_ERROR_MESSAGE, type TurnstileHandle, } from '@/components/Turnstile';
import { useAnalytics } from '@/contexts/Analytics';
import { validatePassword } from '@/utils/passwordPolicy';
import { createClient } from '@/utils/supabase/component';
import { useUser } from '@/utils/useUser';
const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;
// `#auth-sign-up` never reaches the server, so the static HTML is the sign-in
// form. Until React takes over, this marks <html> before the first paint and
// the CSS shows the sign-up variants (heading, password hint, button label)
// instead, so phones don't see "Welcome back" flip to "Create your free
// account" once the page hydrates.
const PRE_HYDRATION_SCRIPT = "try{if(location.hash==='#auth-sign-up')document.documentElement.setAttribute('data-auth-mode','sign-up')}catch(e){}";
const PRE_HYDRATION_CSS = "html[data-auth-mode='sign-up'] .auth-sign-in{display:none!important}html[data-auth-mode='sign-up'] .auth-sign-up.hidden{display:revert!important}";
export default function Login() {
    const supabase = createClient();
    const router = useRouter();
    const { user } = useUser();
    const { t, lang } = useAuthT();
    const track = useAuthTrack('page');
    const gap = useCopyGap();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState<{
        type: 'error' | 'success';
        text: string;
    } | null>(null);
    const [isSignUp, setIsSignUp] = useState(false);
    // Post-signup confirmation state: when set, the auth form is replaced by a
    // "confirm your email" panel with resend / open-inbox / Google-SSO actions.
    const [awaitingVerification, setAwaitingVerification] = useState<string | null>(null);
    const [resendCooldown, setResendCooldown] = useState(0);
    const [resending, setResending] = useState(false);
    // Supabase Auth rejects sign-in/sign-up/resend without a captcha token once
    // Attack Protection is on. The confirmation panel replaces the form, so the
    // resend flow needs its own widget.
    const captchaRef = useRef<TurnstileHandle>(null);
    // Magic-link fallback. Password sign-in can be unavailable for reasons that
    // have nothing to do with the user's credentials (edge rate limiting on the
    // token endpoint, a forgotten password), and the magic-link endpoint is a
    // different route entirely — so this is a genuine second way in.
    const [magicLinkSending, setMagicLinkSending] = useState(false);
    const [magicLinkSent, setMagicLinkSent] = useState(false);
    // Only offered once a password sign-in has actually failed — it is a
    // recovery path, not a primary one. Deliberately NOT offered when the
    // captcha itself failed: the magic-link endpoint is behind the same gate,
    // so the button would be guaranteed to fail too.
    const [showMagicLink, setShowMagicLink] = useState(false);
    const resendCaptchaRef = useRef<TurnstileHandle>(null);
    const [resendMessage, setResendMessage] = useState<{
        type: 'success' | 'error';
        text: string;
    } | null>(null);
    // Set when the Postmark webhook has recorded a hard delivery failure for
    // the address awaiting verification (see /api/verification-delivery-status).
    const [deliveryFailure, setDeliveryFailure] = useState(false);
    const confirmHeadingRef = useRef<HTMLDivElement>(null);
    // Handle query params
    const { price, avatar, preset, headshots_join, join, redirectTo, promotionCode, } = router.query;
    const [, setPriceValue] = useLocalStorage('price');
    const [, setPromotionCodeValue] = useLocalStorage('promotionCode');
    const [, setAvatarValue] = useLocalStorage('avatar');
    const [, setPresetValue] = useLocalStorage('preset');
    const [, setJoinValue] = useLocalStorage('join');
    const [, setHeadshotsJoinValue] = useLocalStorage('headshots_join');
    const [, setRedirectToValue] = useLocalStorage('redirectTo');
    const [storedEmailHistory, setStoredEmailHistory] = useLocalStorage<string[]>('emailHistory', []);
    const analytics = useAnalytics();
    const { lastUsedLoginMethod, setLastUsedLoginMethod } = useLastUsedLoginMethod();
    // Check for hash fragment on mount (for sign up mode). Read from the address
    // bar before the first client paint, then hand over from the pre-hydration
    // CSS (see PRE_HYDRATION_SCRIPT).
    useIsomorphicLayoutEffect(() => {
        if (window.location.hash === '#auth-sign-up') {
            setIsSignUp(true);
        }
        document.documentElement.removeAttribute('data-auth-mode');
    }, []);
    useEffect(() => {
        if (router.asPath.includes('#auth-sign-up')) {
            setIsSignUp(true);
        }
    }, [router.asPath]);
    // Check for error messages in query params
    useEffect(() => {
        const error = router.query.error as string;
        if (error) {
            setMessage({ type: 'error', text: decodeURIComponent(error) });
        }
    }, [router.query.error]);
    // Store query params in localStorage
    useEffect(() => {
        if (price) {
            setPriceValue(price);
        }
    }, [price, setPriceValue]);
    useEffect(() => {
        if (promotionCode)
            setPromotionCodeValue(promotionCode);
    }, [promotionCode, setPromotionCodeValue]);
    useEffect(() => {
        if (avatar)
            setAvatarValue(avatar);
    }, [avatar, setAvatarValue]);
    useEffect(() => {
        if (preset)
            setPresetValue(preset);
    }, [preset, setPresetValue]);
    useEffect(() => {
        if (redirectTo) {
            const target = Array.isArray(redirectTo) ? redirectTo[0] : redirectTo;
            if (target && typeof target === 'string') {
                setRedirectToValue(target);
            }
        }
    }, [redirectTo, setRedirectToValue]);
    useEffect(() => {
        if (join)
            setJoinValue(join);
    }, [join, setJoinValue]);
    useEffect(() => {
        if (headshots_join)
            setHeadshotsJoinValue(headshots_join);
    }, [headshots_join, setHeadshotsJoinValue]);
    // Redirect if user is already logged in
    useEffect(() => {
        if (user) {
            router.push('/dashboard', '/dashboard', { locale: lang });
        }
    }, [user, router, lang]);
    const passwordValidation = validatePassword(password, email);
    const uniqueEmailHistory = buildUniqueEmailHistory(storedEmailHistory);
    const normalizedEmailValue = normalizeEmail(email);
    const hasReachedEmailLimit = uniqueEmailHistory.length >= EMAIL_HISTORY_LIMIT;
    const emailAlreadyTracked = normalizedEmailValue
        ? uniqueEmailHistory.some((entry) => normalizeEmail(entry) === normalizedEmailValue)
        : false;
    const isBlockedByEmailLimit = isSignUp &&
        normalizedEmailValue.length > 0 &&
        hasReachedEmailLimit &&
        !emailAlreadyTracked;
    const emailLimitMessage = `You've reached the limit of ${EMAIL_HISTORY_LIMIT} accounts on this device. Please continue with an existing email or contact support for assistance.`;
    const submitButtonDisabled = loading ||
        (isSignUp && isBlockedByEmailLimit) ||
        (isSignUp && password.length > 0 && !passwordValidation.isValid);
    useEffect(() => {
        if (message?.type === 'error' && message.text === emailLimitMessage) {
            if (!isBlockedByEmailLimit) {
                setMessage(null);
            }
        }
    }, [message, isBlockedByEmailLimit, emailLimitMessage]);
    // Tick down the resend cooldown once per second.
    useEffect(() => {
        if (resendCooldown <= 0)
            return;
        const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
        return () => clearTimeout(timer);
    }, [resendCooldown]);
    const handleMagicLink = async () => {
        const trimmedEmail = email.trim();
        if (!trimmedEmail) {
            setMessage({
                type: 'error',
                text: 'Enter your email address first.',
            });
            return;
        }
        track.started({ mode: 'sign_in', method: 'magic_link' });
        setMagicLinkSending(true);
        setMessage(null);
        try {
            const captchaToken = await captchaRef.current?.getToken();
            if (isTurnstileEnabled && !captchaToken) {
                setMessage({ type: 'error', text: TURNSTILE_ERROR_MESSAGE });
                return;
            }
            const { error } = await supabase.auth.signInWithOtp({
                email: trimmedEmail,
                options: {
                    emailRedirectTo: `${window.location.origin}/auth/confirm?type=magiclink`,
                    // Never create an account from this button — otherwise a typo'd
                    // address silently becomes a second, empty account.
                    shouldCreateUser: false,
                    captchaToken,
                },
            });
            // A missing account errors here, but saying so would let anyone test
            // which emails are registered — so the confirmation is deliberately the
            // same either way.
            if (error && !/signups not allowed|not found/i.test(error.message)) {
                setMessage({ type: 'error', text: error.message });
                return;
            }
            setMagicLinkSent(true);
        }
        catch {
            setMessage({ type: 'error', text: 'An unexpected error occurred' });
        }
        finally {
            captchaRef.current?.reset();
            setMagicLinkSending(false);
        }
    };
    const handleResendVerification = async () => {
        if (!awaitingVerification || resending || resendCooldown > 0)
            return;
        track.started({ mode: 'sign_up', method: 'resend' });
        setResending(true);
        setResendMessage(null);
        try {
            const captchaToken = await resendCaptchaRef.current?.getToken();
            if (isTurnstileEnabled && !captchaToken) {
                setResendMessage({ type: 'error', text: TURNSTILE_ERROR_MESSAGE });
                return;
            }
            const { error } = await supabase.auth.resend({
                type: 'signup',
                email: awaitingVerification,
                options: {
                    emailRedirectTo: `${window.location.origin}/auth/confirm`,
                    captchaToken,
                },
            });
            if (error) {
                setResendMessage({ type: 'error', text: error.message });
            }
            else {
                setResendMessage({
                    type: 'success',
                    text: 'A new confirmation email is on its way.',
                });
                setResendCooldown(60);
            }
        }
        catch {
            setResendMessage({
                type: 'error',
                text: 'Could not resend the email. Please try again.',
            });
        }
        finally {
            // Turnstile tokens are single use — clear it whatever the outcome.
            resendCaptchaRef.current?.reset();
            setResending(false);
        }
    };
    // After signup, check (at 20s and 60s) whether the confirmation email hard
    // bounced, so the user sees "couldn't deliver" instead of a silent dead-end.
    useEffect(() => {
        if (!awaitingVerification) {
            setDeliveryFailure(false);
            return;
        }
        let cancelled = false;
        const check = async () => {
            try {
                const res = await fetch(`/api/verification-delivery-status?email=${encodeURIComponent(awaitingVerification)}`);
                const data = await res.json();
                if (!cancelled && data?.failed)
                    setDeliveryFailure(true);
            }
            catch {
                // Best-effort check only.
            }
        };
        const firstCheck = setTimeout(check, 20000);
        const secondCheck = setTimeout(check, 60000);
        return () => {
            cancelled = true;
            clearTimeout(firstCheck);
            clearTimeout(secondCheck);
        };
    }, [awaitingVerification]);
    const handleSignIn = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setMessage(null);
        try {
            const captchaToken = await captchaRef.current?.getToken();
            if (isTurnstileEnabled && !captchaToken) {
                setMessage({ type: 'error', text: TURNSTILE_ERROR_MESSAGE });
                return;
            }
            const { error } = await supabase.auth.signInWithPassword({
                email,
                password,
                options: { captchaToken },
            });
            if (error) {
                setMessage({ type: 'error', text: error.message });
                // Wrong password, or the request never reached Supabase (an edge rate
                // limit returns a CORS-less block page, which surfaces as "Failed to
                // fetch"). A sign-in link is a genuine way around both.
                setShowMagicLink(true);
            }
            else {
                setLastUsedLoginMethod('email');
                // Redirect will happen automatically via auth state change
                window.location.href =
                    lang !== 'en' ? `/${lang}/dashboard/` : '/dashboard/';
            }
        }
        catch {
            setMessage({ type: 'error', text: 'An unexpected error occurred' });
            setShowMagicLink(true);
        }
        finally {
            // Turnstile tokens are single use — clear it whatever the outcome.
            captchaRef.current?.reset();
            setLoading(false);
        }
    };
    const handleSignUp = async (e: React.FormEvent) => {
        e.preventDefault();
        if (normalizedEmailValue && hasReachedEmailLimit && !emailAlreadyTracked) {
            setMessage({
                type: 'error',
                text: emailLimitMessage,
            });
            return;
        }
        if (!passwordValidation.isValid) {
            setMessage({
                type: 'error',
                text: passwordValidation.firstError ??
                    'Password does not meet the security requirements.',
            });
            return;
        }
        setLoading(true);
        setMessage(null);
        try {
            // Validate email via API before attempting signup
            const response = await fetch('/api/validate-email', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email }),
            });
            const validation = await response.json();
            if (!validation.isValid) {
                setMessage({
                    type: 'error',
                    text: validation.reason || 'Invalid email address',
                });
                setLoading(false);
                return;
            }
            const captchaToken = await captchaRef.current?.getToken();
            if (isTurnstileEnabled && !captchaToken) {
                setMessage({ type: 'error', text: TURNSTILE_ERROR_MESSAGE });
                return;
            }
            const { data, error } = await supabase.auth.signUp({
                email,
                password,
                options: {
                    emailRedirectTo: `${window.location.origin}/auth/confirm`,
                    captchaToken,
                },
            });
            if (error) {
                setMessage({ type: 'error', text: error.message });
            }
            else if ((data.user?.identities?.length ?? 0) === 0) {
                // The email already belongs to a CONFIRMED account (e.g. created via
                // Google): Supabase anti-enumeration returns this fake "success" with
                // an identity-less user and sends NO email — and resend() silently
                // no-ops too — so the "Confirm your email" panel would strand the
                // user waiting for mail that will never arrive. Flip to sign-in with
                // their email kept and surface the passwordless rescue, which works
                // whichever provider the account was created with.
                analytics.track('Sign Up Existing Account', { method: 'email' });
                // Mint a fresh captcha token for the courtesy email BEFORE flipping
                // the view — the flip re-renders the widget with the 'login' action
                // and would void the pending token request.
                captchaRef.current?.reset();
                const noticeToken = await captchaRef.current?.getToken();
                setIsSignUp(false);
                setShowMagicLink(true);
                setMessage({
                    type: 'error',
                    text: EXISTING_ACCOUNT_PAGE,
                });
                // Best-effort: emails the mailbox owner which provider they signed up
                // with plus a one-click sign-in link. Fire-and-forget by design.
                fetch('/api/notify-existing-account', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email, captchaToken: noticeToken }),
                }).catch(() => undefined);
            }
            else {
                // Browser-side twin of the server-fired `Sign Up Success`, which comes
                // from a Supabase DB webhook and therefore carries no session, URL,
                // referrer or UTMs — making landing-page attribution impossible. This
                // one does, so use it as the conversion goal for acquisition tests.
                // Named distinctly so it never double-counts against the server event.
                analytics.track('Sign Up Started', { method: 'email' });
                // Lets the first authenticated load fire `Sign Up Completed` even if
                // the confirmation email is opened hours later (useUser.tsx).
                try {
                    localStorage.setItem('ep_pending_signup', String(Date.now()));
                }
                catch {
                    // storage may be unavailable (private mode) — first-session
                    // detection still covers the common case.
                }
                setStoredEmailHistory((previousValue) => {
                    const existingHistory = buildUniqueEmailHistory(previousValue);
                    if (normalizedEmailValue &&
                        !existingHistory.some((entry) => normalizeEmail(entry) === normalizedEmailValue)) {
                        existingHistory.push(email.trim());
                    }
                    return existingHistory.slice(0, EMAIL_HISTORY_LIMIT);
                });
                // Swap the form for the confirmation panel. The first email just went
                // out, so start the resend cooldown immediately.
                setMessage(null);
                setResendMessage(null);
                setAwaitingVerification(email.trim());
                setResendCooldown(60);
            }
        }
        catch {
            setMessage({ type: 'error', text: 'An unexpected error occurred' });
        }
        finally {
            // Turnstile tokens are single use — clear it whatever the outcome.
            captchaRef.current?.reset();
            setLoading(false);
        }
    };
    const handleOAuthSignIn = async (provider: 'google' | 'facebook' | 'apple') => {
        setLoading(true);
        // Remember the chosen provider before we redirect away to the OAuth flow,
        // so the "Last used" hint is ready when the user returns next time.
        setLastUsedLoginMethod(provider);
        try {
            // Determine the correct redirect URL based on environment
            const redirectTo = typeof window !== 'undefined'
                ? `${window.location.origin}/auth/callback`
                : `${process.env.NEXT_PUBLIC_SITE_URL || 'https://easy-peasy.ai'}/auth/callback`;
            const { error } = await supabase.auth.signInWithOAuth({
                provider,
                options: {
                    redirectTo,
                    queryParams: {
                        access_type: 'offline',
                        prompt: 'consent',
                    },
                },
            });
            if (error) {
                setMessage({ type: 'error', text: error.message });
            }
        }
        catch {
            setMessage({ type: 'error', text: 'An unexpected error occurred' });
        }
        finally {
            setLoading(false);
        }
    };
    const mode = isSignUp ? 'sign_up' : 'sign_in';
    // Text that differs by mode is in the markup twice: state shows one, and
    // before hydration the CSS does (see PRE_HYDRATION_SCRIPT).
    const variant = (forSignUp: boolean) => clsxm(forSignUp ? 'auth-sign-up' : 'auth-sign-in', forSignUp !== isSignUp && 'hidden');
    const described = message ? describeAuthMessage(message.text) : null;
    const messageText = described ? t(described.key, described.values) : '';
    // Every error shown is counted once, by reason (never its text).
    useEffect(() => {
        if (message?.type !== 'error')
            return;
        track.error({ mode, reason: describeAuthMessage(message.text).reason });
        // `mode` is read when the error appears; a later mode switch is no event.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [message, track]);
    useEffect(() => {
        if (resendMessage?.type !== 'error')
            return;
        track.error({
            mode: 'sign_up',
            method: 'resend',
            reason: describeAuthMessage(resendMessage.text).reason,
        });
    }, [resendMessage, track]);
    // The confirmation panel takes the form's place: move focus to its
    // heading so screen readers announce it.
    useEffect(() => {
        if (awaitingVerification)
            confirmHeadingRef.current?.focus();
    }, [awaitingVerification]);
    const switchMode = (toSignUp: boolean) => {
        setIsSignUp(toSignUp);
        setMessage(null);
        track.modeSwitched(toSignUp ? 'sign_up' : 'sign_in');
    };
    const onOAuth = (provider: 'google' | 'facebook') => {
        track.started({ mode, method: provider });
        handleOAuthSignIn(provider);
    };
    const onSubmit = (event: React.FormEvent) => {
        track.started({ mode, method: 'email' });
        return isSignUp ? handleSignUp(event) : handleSignIn(event);
    };
    const inbox = inboxProviderFor(awaitingVerification);
    const gmailNudge = isSignUp && !isBlockedByEmailLimit && inboxProviderFor(email)?.isGoogle;
    const errorAction = (reason: AuthErrorReason) => {
        if (reason === 'invalid_credentials' && !isSignUp) {
            return (<button type='button' onClick={() => switchMode(true)} className={clsxm(linkClass, 'text-[14px]')}>
          {t('New here? Create an account')}
        </button>);
        }
        if (reason === 'gmail_inbox_taken' && isSignUp) {
            return (<button type='button' onClick={() => switchMode(false)} className={clsxm(linkClass, 'text-[14px]')}>
          {t('Sign in instead')}
        </button>);
        }
        return null;
    };
    const errorNotice = (placement: 'top' | 'form') => {
        if (!message || !described)
            return null;
        if (message.type === 'success') {
            return placement === 'form' ? (<Notice kind='success'>{messageText}</Notice>) : null;
        }
        const onTop = isOAuthReason(described.reason);
        if ((placement === 'top') !== onTop)
            return null;
        // The device limit already shows under the email field.
        if (described.reason === 'device_limit' && isBlockedByEmailLimit) {
            return null;
        }
        return (<Notice kind='error' action={errorAction(described.reason)}>
        {messageText}
      </Notice>);
    };
    return (<AuthLayout title={t('Sign In')}>
      <style dangerouslySetInnerHTML={{ __html: PRE_HYDRATION_CSS }}/>
      <script dangerouslySetInnerHTML={{ __html: PRE_HYDRATION_SCRIPT }}/>
      {awaitingVerification && (<div>
          <div ref={confirmHeadingRef} tabIndex={-1} className='outline-none'>
            <AuthHeading icon={<StatusIcon>
                  <MailIcon />
                </StatusIcon>} title={t('Check your inbox')}>
              <p>
                <SentConfirmation email={awaitingVerification}/>
              </p>
            </AuthHeading>
          </div>

          <div className='mt-7 space-y-3'>
            {inbox && (<a href={inbox.url} target='_blank' rel='noopener noreferrer' className={buttonClass('primary')}>
                {t('Open {{provider}}', { provider: inbox.name })}
              </a>)}

            {inbox?.isGoogle && (<div className='rounded-2xl bg-white p-4 ring-1 ring-inset ring-[#0f0f28]/[0.08] dark:bg-white/[0.04] dark:ring-white/10'>
                <p className={clsxm('text-[14px] font-semibold leading-5', tone.ink)}>
                  {t('Skip the wait: this is a Google account')}
                </p>
                <p className={clsxm('mt-1 text-[14px] leading-5', tone.body)}>
                  {t("Continue with Google and you're in right away, with no confirmation email.")}
                </p>
                <AuthButton variant='secondary' className='mt-3.5' onClick={() => onOAuth('google')} disabled={loading}>
                  <GoogleGlyph className='h-5 w-5'/>
                  {t('Continue with {{provider}}', { provider: 'Google' })}
                </AuthButton>
              </div>)}

            {deliveryFailure && (<Notice kind='error'>
                {t("We couldn't deliver the email to this address. It may be misspelled or unable to receive mail. Please use a different email address.")}
              </Notice>)}
          </div>

          <div className='mt-8 border-t border-[#0f0f28]/[0.08] pt-6 dark:border-white/10'>
            <p className={clsxm('text-[14px] leading-5', tone.body)}>
              {t("Didn't get it? Check your spam folder, or:")}
            </p>
            <div className='mt-3 flex flex-wrap items-center gap-x-4 gap-y-2'>
              <button type='button' onClick={handleResendVerification} disabled={resending || resendCooldown > 0} aria-live='polite' className={clsxm(buttonClass('quiet'), 'disabled:no-underline', resendCooldown > 0 && 'tabular-nums')}>
                {resendCooldown > 0
                ? t('Resend in {{seconds}}s', {
                    seconds: resendCooldown,
                })
                : resending
                    ? t('Sending...')
                    : t('Resend email')}
              </button>
              <span aria-hidden='true' className='text-[#0f0f28]/20 dark:text-white/20'>
                ·
              </span>
              <button type='button' onClick={() => {
                setAwaitingVerification(null);
                setResendMessage(null);
            }} className={buttonClass('quiet')}>
                {t('Use a different email')}
              </button>
            </div>

            <AuthTurnstile ref={resendCaptchaRef} action='resend' className='mt-4'/>

            {resendMessage && (<Notice kind={resendMessage.type === 'error' ? 'error' : 'success'} className='mt-4'>
                {t(describeAuthMessage(resendMessage.text).key, describeAuthMessage(resendMessage.text).values)}
              </Notice>)}
          </div>
        </div>)}

      {/* Hidden, not unmounted, while the confirmation panel shows: the
            form's Turnstile widget keeps its state for "Use a different email". */}
      <div className={awaitingVerification ? 'hidden' : undefined}>
        <AuthHeading title={<>
              <span className={variant(true)}>
                {t('Create your free account')}
              </span>
              <span className={variant(false)}>{t('Welcome back')}</span>
            </>}>
          <p className={variant(true)}>
            <Trans i18nKey='Already have an account? <0>Sign in</0>' ns='login' defaultTrans='Already have an account? <0>Sign in</0>' components={[
            <button key='switch' type='button' onClick={() => switchMode(false)} className={linkClass}/>,
        ]}/>
          </p>
          <p className={variant(false)}>
            <Trans i18nKey='New to Easy-Peasy.AI? <0>Create a free account</0>' ns='login' defaultTrans='New to Easy-Peasy.AI? <0>Create a free account</0>' components={[
            <button key='switch' type='button' onClick={() => switchMode(true)} className={linkClass}/>,
        ]}/>
          </p>
        </AuthHeading>

        {errorNotice('top') && <div className='mt-6'>{errorNotice('top')}</div>}

        <div className='mt-7'>
          <OAuthButtons onSelect={onOAuth} disabled={loading} lastUsed={lastUsedLoginMethod} errorKey={message}/>
        </div>

        <div className='my-6'>
          <OrDivider label={t('or')}/>
        </div>

        <form onSubmit={onSubmit}>
          <div className='space-y-5'>
            {isSignUp && hasReachedEmailLimit && (<Notice kind='warning' title={t('Please continue with an existing email')}>
                <p>
                  {t('To keep things secure, each browser can create up to {{limit}} accounts.', { limit: EMAIL_HISTORY_LIMIT })}
                </p>
                {uniqueEmailHistory.length > 0 && (<ul className='mt-2.5 flex flex-wrap gap-1.5'>
                    {uniqueEmailHistory.map((historyEmail) => (<li key={normalizeEmail(historyEmail)} dir='ltr' className='rounded-full bg-white/80 px-2.5 py-1 text-[12px] font-medium ring-1 ring-inset ring-amber-600/20 dark:bg-white/10 dark:ring-amber-300/20'>
                        {historyEmail}
                      </li>))}
                  </ul>)}
              </Notice>)}

            <div>
              <label htmlFor='email' className={labelClass}>
                {t('Email address')}
              </label>
              <input id='email' name='email' type='email' inputMode='email' autoComplete={isSignUp ? 'email' : 'username'} autoCapitalize='none' autoCorrect='off' spellCheck={false} required value={email} onChange={(e) => setEmail(e.target.value)} aria-invalid={isBlockedByEmailLimit || undefined} aria-describedby={isBlockedByEmailLimit
            ? 'email-limit'
            : gmailNudge
                ? 'email-google'
                : undefined} className={clsxm(fieldClass, 'mt-2', isBlockedByEmailLimit && fieldInvalid)} placeholder='name@example.com'/>
              {isBlockedByEmailLimit && (<p id='email-limit' className='mt-2 text-[13px] leading-5 text-red-700 dark:text-red-300'>
                  {t("You've reached the limit of {{limit}} accounts on this device. Please continue with an existing email or contact support for assistance.", { limit: EMAIL_HISTORY_LIMIT })}
                </p>)}
              {gmailNudge && (<div id='email-google' className={clsxm('mt-2.5 flex items-start gap-2.5 text-[13px] leading-5', tone.body)}>
                  <GoogleGlyph className='mt-0.5 h-4 w-4 flex-none'/>
                  <p>
                    {t('Gmail address? Skip the confirmation email:')}
                    {gap}
                    <button type='button' onClick={() => onOAuth('google')} className={linkClass}>
                      {t('sign up with Google instead')}
                    </button>
                  </p>
                </div>)}
            </div>

            <PasswordField id='password' label={t('Password')} value={password} onChange={setPassword} autoComplete={isSignUp ? 'new-password' : 'current-password'} minLength={isSignUp ? 12 : undefined} rules={isSignUp ? passwordValidation.rules : undefined} hintClassName={variant(true)} invalid={isSignUp && password.length > 0
            ? !passwordValidation.isValid
            : undefined} labelAside={<Link href='/auth/forgot-password' className={clsxm(linkClass, 'text-[13px]', variant(false))}>
                  {t('Forgot password?')}
                </Link>}/>
          </div>

          <div className='mt-6'>
            <AuthTurnstile ref={captchaRef} action={isSignUp ? 'signup' : 'login'} className='mb-4'/>

            {errorNotice('form') && (<div className='mb-4'>{errorNotice('form')}</div>)}

            <AuthButton type='submit' loading={loading} disabled={submitButtonDisabled} aria-disabled={submitButtonDisabled} title={isSignUp && isBlockedByEmailLimit
            ? t("You've reached the limit of {{limit}} accounts on this device. Please continue with an existing email or contact support for assistance.", { limit: EMAIL_HISTORY_LIMIT })
            : undefined}>
              {loading ? (isSignUp ? (t('Creating your account...')) : (t('Signing in...'))) : (<>
                  <span className={variant(true)}>{t('Create account')}</span>
                  <span className={variant(false)}>{t('Sign in')}</span>
                </>)}
              {!isSignUp && lastUsedLoginMethod === 'email' && !loading && (<LastUsedBadge label={t('Last used')} variant='onPrimary'/>)}
            </AuthButton>

            {!isSignUp &&
            showMagicLink &&
            (magicLinkSent ? (<Notice kind='success' className='mt-4'>
                  {t("If that email has an account, we've sent a sign-in link. Check your inbox.")}
                </Notice>) : (<div className='mt-4 rounded-2xl bg-white p-4 text-center ring-1 ring-inset ring-[#0f0f28]/[0.08] dark:bg-white/[0.04] dark:ring-white/10'>
                  <p className={clsxm('text-[14px] leading-5', tone.body)}>
                    {t("Can't get in? Sign in without your password.")}
                  </p>
                  <AuthButton variant='secondary' className='mt-3' onClick={handleMagicLink} loading={magicLinkSending} disabled={magicLinkSending || loading}>
                    {magicLinkSending
                    ? t('Sending...')
                    : t('Email me a sign-in link')}
                  </AuthButton>
                </div>))}
          </div>
        </form>

        <TermsNote className='mt-8'/>
      </div>
    </AuthLayout>);
}

    async function __Next_Translate__getStaticProps__1a1a4b3e7a3__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      
      return {
        
        
        props: {
          
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/login',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a1a4b3e7a3__ as getStaticProps }
  